// view.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Typography,
  CardContent,
  Avatar,
  InputBase,
  Tabs,
  Tab,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  TextField,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Card,
  CardHeader,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import PendingIcon from '@mui/icons-material/Pending';
import QuizIcon from '@mui/icons-material/Quiz';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import dayjs from 'dayjs';
import MemberCard from '../memberCard';
import {
  MembersContainer,
  SearchBox,
  InviteButton,
  FilterTabsContainer,
  StyledDialog,
  StyledDialogTitle,
  StyledDialogContent,
  StyledDialogActions,
  StyledMenu,
  EmptyStateContainer
} from './styled';

const MembersTabView = ({ members = [], isAdmin = false, onMemberClick, onInviteMember, onApproveMember, onRejectMember }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTab, setFilterTab] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('PA');
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [reviewMember, setReviewMember] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [isApproving, setIsApproving] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [processedMembers, setProcessedMembers] = useState([]);

  // Update processed members when props change
  useEffect(() => {
    const processed = members.map(member => ({
      ...member,
      name: member.user ?
        `${member.user.first_name || ''} ${member.user.last_name || ''}`.trim() || 'Unknown Member' :
        member.name || 'Unknown Member',
      firstName: member.user?.first_name || '',
      lastName: member.user?.last_name || '',
      email: member.user?.email || '',
      bio: member.user?.description || member.bio || '',
      location: member.user?.location || member.location || '',
      avatar: member.user?.avatar || member.avatar || '',
      role: member.role || 'PA',
      status: member.status || 'AC',
      slug: member.slug || 'unidentified',
      isVerified: member.user?.is_verified || member.isVerified || false,
      isOnline: member.id ? (member.id % 3 === 0) : Math.random() > 0.5,
      lastActivity: member.create_date ?
        dayjs(member.create_date).add(Math.floor(Math.random() * 48), 'hour').valueOf() :
        dayjs().subtract(Math.floor(Math.random() * 48), 'hour').valueOf(),
      joinedDate: member.create_date || dayjs().subtract(30, 'day').format(),
      admissionAnswers: member.admission_answers || []
    }));
    setProcessedMembers(processed);
  }, [members]);

  const updateMemberStatus = (memberSlug, newStatus, newRole = null) => {
    setProcessedMembers(prevMembers =>
      prevMembers.map(member =>
        member.slug === memberSlug
          ? {
            ...member,
            status: newStatus,
            ...(newRole && { role: newRole })
          }
          : member
      )
    );
  };

  const handleMenuOpen = (event, member) => {
    setAnchorEl(event.currentTarget);
    setSelectedMember(member);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleInviteOpen = () => {
    setInviteDialogOpen(true);
  };

  const handleInviteClose = () => {
    setInviteDialogOpen(false);
    setInviteEmail('');
    setInviteRole('PA');
  };

  const handleInviteSubmit = () => {
    if (onInviteMember && inviteEmail.trim()) {
      onInviteMember({
        email: inviteEmail.trim(),
        role: inviteRole
      });
    }
    handleInviteClose();
  };

  const handleReviewMember = (member) => {
    setReviewMember(member);
    setReviewDialogOpen(true);
    handleMenuClose();
  };

  const handleCloseReview = () => {
    setReviewDialogOpen(false);
    setReviewMember(null);
    setRejectReason('');
  };

  const handleApproveMember = async () => {
    if (onApproveMember && reviewMember) {
      setIsApproving(true);
      try {
        const result = await onApproveMember(reviewMember.slug);

        if (result && result.success) {

          // Update the member status immediately
          updateMemberStatus(reviewMember.slug, 'AC');

          // Close the dialog
          handleCloseReview();
        }
      } catch (error) {
        console.error('Error approving member:', error);
        // Handle error (show toast, etc.)
      } finally {
        setIsApproving(false);
      }
    }
  };

  const handleRejectMember = async () => {
    if (onRejectMember && reviewMember) {
      setIsRejecting(true);
      try {
        const result = await onRejectMember(reviewMember.slug, rejectReason);

        if (result && result.success) {

          // Update the member status immediately
          updateMemberStatus(reviewMember.slug, 'RE');

          // Close the dialog
          handleCloseReview();
        }
      } catch (error) {
        console.error('Error rejecting member:', error);
        // Handle error
      } finally {
        setIsRejecting(false);
      }
    }
  };

  // Enhanced filter logic to include status filtering
  const filteredMembers = processedMembers.filter(member => {
    const searchLower = (searchTerm || '').toLowerCase();
    const matchesSearch = !searchTerm ||
      (member.name || '').toLowerCase().includes(searchLower) ||
      (member.firstName || '').toLowerCase().includes(searchLower) ||
      (member.lastName || '').toLowerCase().includes(searchLower) ||
      (member.email || '').toLowerCase().includes(searchLower) ||
      (member.bio || '').toLowerCase().includes(searchLower) ||
      (member.location || '').toLowerCase().includes(searchLower);

    // Tab filters
    if (filterTab === 1) { // Pending members
      return matchesSearch && member.status === 'PP';
    }
    if (filterTab === 2) { // Active members
      return matchesSearch && member.status === 'AC';
    }
    if (filterTab === 3) { // Community Rejected§ members
      return matchesSearch && member.status === 'RE';
    }

    return matchesSearch;
  });

  const getTimeAgo = (timestamp) => {
    const now = dayjs();
    const time = dayjs(timestamp);
    const diffHours = now.diff(time, 'hour');

    if (diffHours < 1) return 'Just now';
    if (diffHours < 24) return `${diffHours}h ago`;

    const diffDays = now.diff(time, 'day');
    if (diffDays < 7) return `${diffDays}d ago`;

    return time.format('MMM D');
  };

  const memberActions = [
    { label: 'Message', action: (member) => onMemberClick && onMemberClick(member, 'message') },
    ...(isAdmin ? [
      ...(selectedMember?.status === 'PP' ? [
        { label: 'Review Application', action: handleReviewMember, primary: true },
      ] : []),
      { label: 'Change Role', action: (member) => onMemberClick && onMemberClick(member, 'role') },
      { label: 'Remove from Community', action: (member) => onMemberClick && onMemberClick(member, 'remove'), danger: true },
    ] : [])
  ];

  // Count members for tabs
  const pendingCount = processedMembers.filter(m => m.status === 'PP').length;
  const activeCount = processedMembers.filter(m => m.status === 'AC').length;
  const rejectedCount = processedMembers.filter(m => m.status === 'RE').length;

  // Role names for dialogs
  const roleNames = {
    'FO': 'Founder',
    'AD': 'Admin',
    'MA': 'Manager',
    'PT': 'Partner',
    'EO': 'Event Organizer',
    'PA': 'Member',
    'GU': 'Guest'
  };

  return (
    <MembersContainer>
      <Box className="members-header">
        <Box className="search-invite-row" sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <SearchBox sx={{ flex: 1 }}>
            <SearchIcon />
            <InputBase
              fullWidth
              placeholder="Search members..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </SearchBox>
          
          {/* Invite Member Button - Only for admins */}
          {isAdmin && (
            <InviteButton
              variant="contained"
              startIcon={<PersonAddIcon />}
              onClick={handleInviteOpen}
            >
              Invite Member
            </InviteButton>
          )}
        </Box>

        <FilterTabsContainer>
          <Tabs
            value={filterTab}
            onChange={(_, newValue) => setFilterTab(newValue)}
            aria-label="member filter tabs"
          >
            <Tab label={`All (${processedMembers.length})`} />
            {isAdmin && <Tab label={`Pending (${pendingCount})`} />}
            {isAdmin && <Tab label={`Active (${activeCount})`} />}
            {isAdmin && <Tab label={`Rejected (${rejectedCount})`} />}
          </Tabs>
        </FilterTabsContainer>
      </Box>

      <Grid container spacing={3}>
        {filteredMembers.map(member => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={member.id || Math.random()}>
            <MemberCard
              member={member}
              isAdmin={isAdmin}
              onMemberClick={onMemberClick}
              onMenuOpen={handleMenuOpen}
              onReviewMember={handleReviewMember}
              getTimeAgo={getTimeAgo}
            />
          </Grid>
        ))}
      </Grid>

      {filteredMembers.length === 0 && (
        <EmptyStateContainer>
          <Typography variant="h6">
            No members found
          </Typography>
          <Typography variant="body2">
            {searchTerm ? 'Try adjusting your search terms' : 'Try adjusting your filters'}
          </Typography>
        </EmptyStateContainer>
      )}

      {/* Member actions menu */}
      <StyledMenu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        {memberActions.map((action, index) => (
          <MenuItem
            key={index}
            className={action.danger ? 'danger' : action.primary ? 'primary' : ''}
            onClick={() => {
              action.action(selectedMember);
              handleMenuClose();
            }}
          >
            {action.label}
          </MenuItem>
        ))}
      </StyledMenu>

      {/* Review Member Dialog */}
      <StyledDialog open={reviewDialogOpen} onClose={handleCloseReview} maxWidth="md" fullWidth>
        <StyledDialogTitle>
          <QuizIcon />
          Review Application - {reviewMember?.name}
        </StyledDialogTitle>
        <StyledDialogContent>
          {reviewMember && (
            <Box>
              {/* Member Info */}
              <Card sx={{ mb: 3 }}>
                <CardHeader
                  avatar={
                    <Avatar src={reviewMember.avatar}>
                      {reviewMember.name?.charAt(0).toUpperCase()}
                    </Avatar>
                  }
                  title={reviewMember.name}
                  subheader={`Applied on ${dayjs(reviewMember.joinedDate).format('MMMM D, YYYY')}`}
                />
                <CardContent>
                  <Typography variant="body2" color="text.secondary">
                    Email: {reviewMember.user?.email || reviewMember.email}
                  </Typography>
                  {reviewMember.bio && (
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      Bio: {reviewMember.bio}
                    </Typography>
                  )}
                  {reviewMember.location && (
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      Location: {reviewMember.location}
                    </Typography>
                  )}
                </CardContent>
              </Card>

              {/* Admission Answers */}
              {reviewMember.admissionAnswers && reviewMember.admissionAnswers.length > 0 ? (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Admission Questions & Answers
                  </Typography>
                  {reviewMember.admissionAnswers.map((answer, index) => (
                    <Accordion key={index} sx={{ mb: 1 }}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="subtitle2">
                          {answer.question_text || `Question ${index + 1}`}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Typography variant="body2" color="text.secondary">
                          {answer.answer_value || answer.text_answer || 'No answer provided'}
                        </Typography>
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </Box>
              ) : (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    No admission questions were answered for this application.
                  </Typography>
                </Box>
              )}

              {/* Rejection Reason */}
              <TextField
                fullWidth
                label="Rejection Reason (optional)"
                multiline
                rows={3}
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Provide a reason if rejecting this application..."
                sx={{ mb: 2 }}
              />
            </Box>
          )}
        </StyledDialogContent>
        <StyledDialogActions>
          <Button onClick={handleCloseReview}>Cancel</Button>
          <Button
            onClick={() => handleRejectMember(reviewMember.slug, rejectReason)}
            color="error"
            startIcon={<CancelIcon />}
          >
            Reject
          </Button>
          <Button
            onClick={(e) => {
              e.stopPropagation(); // Prevent card click
              handleApproveMember(reviewMember.slug, rejectReason);
            }}
            variant="contained"
            color="success"
            startIcon={<CheckCircleIcon />}
          >
            Approve
          </Button>
        </StyledDialogActions>
      </StyledDialog>

      {/* Invite Member Dialog */}
      <StyledDialog open={inviteDialogOpen} onClose={handleInviteClose} maxWidth="xs" fullWidth>
        <StyledDialogTitle>
          <PersonAddIcon />
          Invite New Member
        </StyledDialogTitle>
        <StyledDialogContent>
          <TextField
            autoFocus
            label="Email Address"
            type="email"
            fullWidth
            variant="outlined"
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
          />
          <FormControl fullWidth variant="outlined">
            <InputLabel>Role</InputLabel>
            <Select
              value={inviteRole}
              onChange={(e) => setInviteRole(e.target.value)}
              label="Role"
            >
              <MenuItem value="PA">Member</MenuItem>
              <MenuItem value="MA">Manager</MenuItem>
              {isAdmin && <MenuItem value="AD">Admin</MenuItem>}
            </Select>
          </FormControl>
        </StyledDialogContent>
        <StyledDialogActions>
          <Button onClick={handleInviteClose}>Cancel</Button>
          <Button
            onClick={handleInviteSubmit}
            variant="contained"
            disabled={!inviteEmail.trim()}
          >
            Send Invitation
          </Button>
        </StyledDialogActions>
      </StyledDialog>
    </MembersContainer>
  );
};

export default MembersTabView;